import { useState } from "react";
import { Radio, Square, StickyNote, FolderInput, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { wholeStory } from "@/lib/storyRead";

// One kept story: why I kept it, which folder it lives in, and MiLu reading it out.
export const SavedStory = ({ story, speaker, folders, onChanged, onRemove }) => {
  const [note, setNote] = useState(story.note || "");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const saveNote = async () => {
    setBusy(true);
    try {
      await api.patch(`/news/saved/${story.story_id}/note`, { note });
      toast.success(note.trim() ? "Note saved" : "Note cleared");
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that note");
    } finally {
      setBusy(false);
    }
  };

  const move = async (name) => {
    try {
      await api.patch(`/news/saved/${story.story_id}/folder`, { name });
      toast.success(name ? `Moved to ${name}` : "Moved out of folders");
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not move that story");
    }
  };

  const reading = speaker.playing === `${story.story_id}-read`;

  return (
    <div className="rounded-2xl border border-white/10 bg-panel p-4" data-testid={`saved-card-${story.story_id}`}>
      <a href={story.url} target="_blank" rel="noreferrer"
         className="text-base font-semibold hover:text-brand" dir="auto">
        {story.title}
      </a>
      <p className="mt-1 text-xs text-ink-soft">
        {story.source}
        {story.read_min ? (
          <span data-testid={`saved-read-min-${story.story_id}`} className="text-brand">
            {` · ${story.read_est ? "~" : ""}${story.read_min} min read`}
          </span>
        ) : null}
      </p>
      {story.original_title && (
        <p className="mt-1 text-xs italic text-ink-soft" dir="auto">{story.original_title}</p>
      )}

      {story.note && !open && (
        <p className="mt-2 rounded-xl bg-white/5 px-3 py-2 text-sm text-ink-soft"
           data-testid={`saved-note-text-${story.story_id}`} dir="auto">
          {story.note}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          data-testid={`saved-listen-${story.story_id}`}
          onClick={() => speaker.play(`${story.story_id}-read`, wholeStory(story))}
          className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-white/10 px-3 text-xs text-ink-soft hover:border-brand/50 hover:text-ink"
        >
          {reading ? <Square className="h-3.5 w-3.5 text-brand" /> : <Radio className="h-3.5 w-3.5 text-brand" />}
          {reading ? "Stop" : "Listen"}
        </button>
        <button
          data-testid={`saved-note-toggle-${story.story_id}`}
          onClick={() => setOpen((o) => !o)}
          className={`inline-flex min-h-[32px] items-center gap-1.5 rounded-full border px-3 text-xs transition-colors ${
            story.note ? "border-brand/50 text-brand" : "border-white/10 text-ink-soft hover:border-brand/50 hover:text-ink"
          }`}
        >
          <StickyNote className="h-3.5 w-3.5" />
          {story.note ? "Edit note" : "Add a note"}
        </button>
        <span className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-white/10 px-3 text-xs text-ink-soft">
          <FolderInput className="h-3.5 w-3.5 text-brand" />
          <select
            data-testid={`saved-folder-${story.story_id}`}
            value={story.folder || ""}
            onChange={(e) => move(e.target.value)}
            className="bg-transparent text-xs text-ink outline-none"
          >
            <option value="">Unsorted</option>
            {folders.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </span>
        <button
          data-testid={`saved-remove-${story.story_id}`}
          onClick={() => onRemove(story)}
          className="inline-flex min-h-[32px] items-center rounded-full border border-white/10 px-3 text-xs text-ink-soft hover:border-brand/50 hover:text-ink"
        >
          Remove
        </button>
      </div>

      {open && (
        <div className="mt-3" data-testid={`saved-note-box-${story.story_id}`}>
          <textarea
            data-testid={`saved-note-input-${story.story_id}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={600}
            rows={2}
            dir="auto"
            placeholder="Why did you keep this? A line for future you…"
            className="w-full rounded-xl border border-white/10 bg-page px-3 py-2 text-sm outline-none focus:border-brand/50"
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              data-testid={`saved-note-save-${story.story_id}`}
              onClick={saveNote}
              disabled={busy}
              className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full bg-brand px-4 text-xs font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95 disabled:opacity-50"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save note
            </button>
            <span className="text-[11px] text-ink-soft">{note.length}/600</span>
          </div>
        </div>
      )}
    </div>
  );
};
